// The colourway swap ('product:swap') replaces the details column — and the
// sticky bar markup inside it — so the observers and element refs are kept
// module-scoped and rebuilt on each init.
let observer: IntersectionObserver | null = null;
let stickyBuyImage: Element | null = null;
let editorialCleanup: (() => void) | null = null;

/** Follow the page price, with a small return buffer to avoid subpixel scroll flicker. */
export function pricePassedViewport(priceBottom: number, previouslyPassed: boolean): boolean {
  return Number.isFinite(priceBottom) && priceBottom <= (previouslyPassed ? 8 : 0);
}

function initEditorialSticky(sticky: HTMLElement, buttons: Element): void {
  let frame = 0;
  let passedPrice = false;
  const product = sticky.closest<HTMLElement>('main.product');
  const price = buttons.closest('.single-product__details')?.querySelector<HTMLElement>('.single-product__price');
  const controls = () => sticky.querySelectorAll<HTMLElement>('button, a, [data-sticky-affirm][tabindex]');
  const blocked = () => {
    const active = document.activeElement as HTMLElement | null;
    if (active && !sticky.contains(active) && (active.matches('input, select, textarea') || active.isContentEditable)) return true;
    return Array.from(document.querySelectorAll<HTMLElement>('dialog[open], .modal--is-visible, .nav.open, .nav__dropdown.open, .nav--search-open, [aria-modal="true"]')).some(element => element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden');
  };
  const update = () => {
    frame = 0;
    // The price is in normal document flow. Unlike main-button visibility, its
    // top-edge crossing cannot switch back as the buttons enter the viewport,
    // the header slides, or the sticky Affirm quote changes the footer height.
    const followsPrice = sticky.hasAttribute('data-sticky-early') && !!price;
    if (followsPrice) {
      const rect = price!.getBoundingClientRect();
      passedPrice = rect.width > 0 && rect.height > 0 && pricePassedViewport(rect.bottom, passedPrice);
    }
    const visible = (followsPrice ? passedPrice : buttons.getBoundingClientRect().bottom <= 0) && !blocked();
    if (document.body.classList.contains('editorial-sticky-active') !== visible) document.body.classList.toggle('editorial-sticky-active', visible);
    if (sticky.classList.contains('sticky-buy--visible') !== visible) sticky.classList.toggle('sticky-buy--visible', visible);
    if (sticky.getAttribute('aria-hidden') !== String(!visible)) sticky.setAttribute('aria-hidden', String(!visible));
    if (visible) sticky.removeAttribute('inert'); else sticky.setAttribute('inert', '');
    controls().forEach(control => {
      if (visible && control.hasAttribute('data-sticky-affirm')) control.setAttribute('tabindex', '0');
      else if (visible) control.removeAttribute('tabindex');
      else control.setAttribute('tabindex', '-1');
    });
  };
  const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
  sticky.setAttribute('inert', '');
  controls().forEach(control => control.setAttribute('tabindex', '-1'));
  const resize = new ResizeObserver(() => {
    document.body.style.setProperty('--editorial-sticky-height', `${Math.ceil(sticky.getBoundingClientRect().height)}px`);
    if (product) product.style.paddingBottom = `${Math.ceil(sticky.getBoundingClientRect().height) + 16}px`;
    schedule();
  });
  resize.observe(sticky);
  if (price) resize.observe(price);
  const overlays = new MutationObserver(schedule);
  overlays.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'open', 'style', 'aria-modal'] });
  observer = new IntersectionObserver(schedule);
  observer.observe(price || buttons);
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  document.addEventListener('focusin', schedule);
  document.addEventListener('focusout', schedule);
  window.visualViewport?.addEventListener('resize', schedule);
  editorialCleanup = () => {
    if (frame) cancelAnimationFrame(frame);
    resize.disconnect(); overlays.disconnect();
    window.removeEventListener('scroll', schedule);
    window.removeEventListener('resize', schedule);
    document.removeEventListener('focusin', schedule);
    document.removeEventListener('focusout', schedule);
    window.visualViewport?.removeEventListener('resize', schedule);
    product?.style.removeProperty('padding-bottom');
    document.body.classList.remove('editorial-sticky-active');
    document.body.style.removeProperty('--editorial-sticky-height');
  };
  schedule();
}

const initStickyBuy = () => {
  // Observers from a previous product's DOM watch detached nodes — drop them
  observer?.disconnect();
  editorialCleanup?.();
  editorialCleanup = null;
  observer = null;
  stickyBuyImage = null;

  const stickyBuyBar = document.querySelector('.sticky-buy');
  const buttonSection = document.querySelector('.single-product__buttons');
  stickyBuyImage = stickyBuyBar?.querySelector('.sticky-buy__image') || null;
  const productTitle = document.querySelector('.single-product__title');
  if (!stickyBuyBar || !buttonSection || !stickyBuyImage || !productTitle) {
    return;
  }

  if (stickyBuyBar instanceof HTMLElement && stickyBuyBar.hasAttribute('data-editorial-sticky')) {
    initEditorialSticky(stickyBuyBar, buttonSection);
    return;
  }

  /** Disable/enable tabbing on any buttons/links in the sticky buy bar */
  const toggleButtonTabbing = (allowTabbing: boolean) => {
    stickyBuyBar.querySelectorAll('button, a').forEach((el) => {
      const tabindex = allowTabbing ? '0' : '-1';
      el.setAttribute('tabindex', tabindex);
    });
  };

  // Disable tabbing initially since the bar is hidden
  toggleButtonTabbing(false);

  /**
   * Keep track of if the title and buttons are visible,
   * so we can show the sticky buy bar when both are offscreen
   */
  const elementVisibility = {
    title: false,
    buttons: false,
  };

  /**
   * Add intersection observer to watch for when the user scrolls past the trigger point (button section),
   * then show/hide the sticky buy bar accordingly
   */
  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const whichElement = entry.target.classList.contains('single-product__buttons')
        ? 'buttons'
        : 'title';
      // Update the visibility object based on whether the element is intersecting
      elementVisibility[whichElement] = entry.isIntersecting;

      // Check if both elements are offscreen
      if (Object.values(elementVisibility).every((visible) => !visible)) {
        stickyBuyBar.classList.add('sticky-buy--visible');
        stickyBuyBar.setAttribute('aria-hidden', 'false');
        toggleButtonTabbing(true);
      } else {
        stickyBuyBar.classList.remove('sticky-buy--visible');
        stickyBuyBar.setAttribute('aria-hidden', 'true');
        toggleButtonTabbing(false);
      }
    });
  });
  observer.observe(buttonSection);
  observer.observe(productTitle);
};

document.addEventListener('DOMContentLoaded', initStickyBuy);
document.addEventListener('product:swap', initStickyBuy);

// Listen to the custom event to update the sticky buy image
// This is emitted in gallery.ts, when the Flickity gallery changes slide.
// Registered once — always targets the current bar's image element.
document.addEventListener('product:image-change', (event: CustomEvent) => {
  // Paired Concierge keeps the complete selected-variant hero; gallery detail
  // shots and video posters must not turn its small thumbnail into a crop.
  if (stickyBuyImage?.closest('[data-paired-concierge-sticky]')) return;
  const { image } = event.detail;
  // console.debug('Product image has changed', image);
  // Setting data-src as well so it doesn't replace with placeholder on initial load
  stickyBuyImage?.setAttribute('data-src', image);
  stickyBuyImage?.setAttribute('src', image);
});
